import { Button, style } from '@do/walrus';
import { useRouter } from 'next/router';
import React from 'react';
import styled from 'styled-components';

import { getAppBySafeName_getAppBySafeName_app as App } from '../../graphql/generated-types/getAppBySafeName';
import {
  getAccessPath,
  getGettingStarted,
  getName,
  getPort,
  getQuickAccessEnabled
} from '../../selectors';
import { isSaasAffiliate, isValidIp } from '../../utils';
import FormatCodeInHtml from '../FormatCodeInHtml';
import IFrame from '../IFrame';
import copy from '../copy';

export const GETTING_STARTED_HASH = 'getting-started';

interface Props {
  app: App;
  isInIframe?: boolean;
}

const PENDO_ID = 'quick-access-button';

export const HTTP_PROTOCOL_URL = 'http://your_droplet_public_ipv4';
export const HTTPS_PROTOCOL_URL = 'https://your_droplet_public_ipv4';

function getQuickAccessUrl(ipAddress: string, port?: number, path?: string) {
  let quickAccessUrl = `http://${ipAddress}`;

  if (port) {
    quickAccessUrl += `:${port}`;
  }

  if (path) {
    quickAccessUrl += `/${path}`;
  }

  return quickAccessUrl;
}

const QuickAccessButton = styled(Button).attrs({
  'data-pendo-id': PENDO_ID
})`
  display: inline-flex;
  align-items: center;
  margin-bottom: ${style.vars.space['3']};
`;

const GettingStarted: React.FC<Props> = ({ app, isInIframe }) => {
  const router = useRouter();
  const name = getName(app);
  let gettingStarted = getGettingStarted(app);
  const isQuickAccessEnabled = getQuickAccessEnabled(app);
  const port = getPort(app);
  const accessPath = getAccessPath(app);

  if (!gettingStarted) {
    return null;
  }

  let ipAddress = router?.query?.ipAddress;

  /**
   * If we returns multiple IP addresses, we only use the first one.
   */
  if (Array.isArray(ipAddress)) {
    ipAddress = ipAddress[0];
  }

  if (ipAddress && isValidIp(ipAddress)) {
    gettingStarted = gettingStarted.replace(
      /@your_droplet_public_ipv4/g,
      `@${ipAddress}`
    );

    const hasHttpProtocolUrl = gettingStarted.includes(HTTP_PROTOCOL_URL);
    const hasHttpsProtocolUrl = gettingStarted.includes(HTTPS_PROTOCOL_URL);

    if (hasHttpProtocolUrl) {
      gettingStarted = gettingStarted.replace(
        HTTP_PROTOCOL_URL,
        `http://${ipAddress}`
      );
    }

    if (hasHttpsProtocolUrl) {
      gettingStarted = gettingStarted.replace(
        HTTPS_PROTOCOL_URL,
        `https://${ipAddress}`
      );
    }
  }

  const canShowQuickAccess = isInIframe && ipAddress && isQuickAccessEnabled;
  const quickAccessUrl = canShowQuickAccess
    ? getQuickAccessUrl(ipAddress, port, accessPath)
    : null;

  const iframeId = `${name}-${GETTING_STARTED_HASH}-frame`;

  return (
    <>
      <h2 id={GETTING_STARTED_HASH}>
        {isSaasAffiliate(app)
          ? copy.gettingStarted.afterAccountCreation
          : copy.gettingStarted.afterDeploying(name)}
      </h2>

      {quickAccessUrl && (
        <QuickAccessButton url={quickAccessUrl} external>
          {copy.gettingStarted.quickAccessTo(name)}
        </QuickAccessButton>
      )}

      <IFrame title={copy.appDetails.gettingStartedTitle} id={iframeId}>
        <FormatCodeInHtml htmlStr={gettingStarted} />
      </IFrame>
    </>
  );
};

export default GettingStarted;
